/**
 * Mirror surety bond status onto the client portal.
 *
 * Portal users cannot read `suretyBondRequests`, so the CRM writes a
 * client-safe `PortalProjectBond` onto `clientPortals/{id}` — nested under
 * `projects.{dealId}.bond` when the portal has that project row, otherwise at
 * the root (single-project portals), matching the installment mirror.
 */

import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { SuretyBondRequest } from '../types/regulatory';
import { findPortalIdForDeal } from './portalFileReleaseService';
import { pickPortalBondRequest, portalBondFromRequest } from './portalBondStatus';

/**
 * Recompute and write the portal bond for one deal from every request on it.
 * Resolves false when the deal has no portal (nothing to update).
 */
export async function mirrorSuretyBondToPortal(
  dealId: string,
  requestsForDeal: SuretyBondRequest[],
): Promise<boolean> {
  if (!dealId) return false;
  const portalId = await findPortalIdForDeal(dealId);
  if (!portalId) return false;

  const ref = doc(db, 'clientPortals', portalId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return false;
  const data = snap.data() as { projects?: Record<string, unknown> };
  const nested = !!data.projects && Object.prototype.hasOwnProperty.call(data.projects, dealId);

  const chosen = pickPortalBondRequest(requestsForDeal.filter((r) => r.dealId === dealId));
  const bond = chosen ? portalBondFromRequest(chosen) : null;
  const now = serverTimestamp();

  if (nested) {
    await updateDoc(ref, {
      [`projects.${dealId}.bond`]: bond,
      [`projects.${dealId}.updatedAt`]: now,
      updatedAt: now,
    });
  } else {
    await updateDoc(ref, { bond, updatedAt: now });
  }
  return true;
}
